import { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { toast } from "sonner";
import { 
  CheckCircle, 
  ArrowRight, 
  Sparkles,
  Clock,
  Target,
  Heart,
  Send,
  Phone,
  Mail,
  User
} from "lucide-react";

import { API } from "../utils/api";

const LandingFlow = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    paymentOption: "comptant",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    setSubmitting(true);
    
    try {
      const response = await fetch(`${API}/landing/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success("Votre demande a été envoyée !");
      } else {
        toast.error("Une erreur est survenue, veuillez réessayer");
      }
    } catch (error) {
      toast.error("Une erreur est survenue, veuillez réessayer");
    } finally {
      setSubmitting(false);
    }
  };

  const benefits = [
    { icon: Target, text: "6 modules progressifs sur 3 mois" },
    { icon: Clock, text: "12 jours entre chaque module" },
    { icon: Heart, text: "Accompagnement personnalisé" },
    { icon: Sparkles, text: "Un RDV de démarrage de 2h avec votre coach" },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#101E4D] via-[#1a2d5c] to-[#101E4D] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#101E4D] mb-4">
            Merci pour votre intérêt !
          </h2>
          <p className="text-slate-600 mb-6">
            Nous avons bien reçu votre demande. Un membre de notre équipe vous contactera très prochainement pour échanger sur votre projet.
          </p>
          <p className="text-sm text-slate-500">
            À très bientôt !
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#101E4D] via-[#1a2d5c] to-[#101E4D]">
      {/* Header */}
      <header className="py-6 px-4">
        <div className="max-w-6xl mx-auto flex justify-center">
          <div className="text-white">
            <span className="text-3xl font-bold">FLOW</span>
            <span className="text-sm ml-2 text-slate-300">by WEDO coaching</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 pb-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            
            {/* Left - Info Section */}
            <div className="text-white space-y-8">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold mb-4 leading-tight">
                  Trouvez votre <span className="text-[#FF6B35]">FLOW</span>
                </h1>
                <p className="text-xl text-slate-300">
                  Un parcours de 3 mois pour développer votre leadership authentique et votre énergie durable.
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                {benefits.map((benefit) => (
                  <div key={benefit.text.slice(0, 20)} className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-[#FF6B35]/20 rounded-lg flex items-center justify-center">
                      <benefit.icon className="w-5 h-5 text-[#FF6B35]" />
                    </div>
                    <span className="text-slate-200">{benefit.text}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <p className="text-slate-300 mb-2">Investissement</p>
                <div className="flex items-baseline gap-2">
                  <span className="text-4xl font-bold text-white">1 290€</span>
                  <span className="text-slate-300">TTC</span>
                </div>
                <p className="text-sm text-slate-400 mt-2">
                  Paiement comptant ou en plusieurs fois
                </p>
              </div>

              {/* Quote */}
              <blockquote className="border-l-4 border-[#FF6B35] pl-4 italic text-slate-300">
                "Tu n'as pas besoin d'en faire plus. Tu as besoin de faire juste."
              </blockquote>
            </div>

            {/* Right - Form Section */}
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <h2 className="text-2xl font-bold text-[#101E4D] mb-2">
                Commencez l'aventure
              </h2>
              <p className="text-slate-500 mb-6">
                Remplissez ce formulaire et nous vous recontacterons rapidement.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="firstName" className="text-slate-700">Prénom</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="firstName"
                        value={formData.firstName}
                        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                        placeholder="Votre prénom"
                        className="pl-10"
                        required
                        data-testid="landing-firstname"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName" className="text-slate-700">Nom</Label>
                    <Input
                      id="lastName"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      placeholder="Votre nom"
                      required
                      data-testid="landing-lastname"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="votre@email.com"
                      className="pl-10"
                      required
                      data-testid="landing-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700">Téléphone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="06 12 34 56 78"
                      className="pl-10"
                      required
                      data-testid="landing-phone"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-slate-700">Mode de paiement souhaité</Label>
                  <RadioGroup
                    value={formData.paymentOption}
                    onValueChange={(value) => setFormData({ ...formData, paymentOption: value })}
                    className="space-y-2"
                  >
                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#FF6B35] transition-colors cursor-pointer">
                      <RadioGroupItem value="comptant" id="comptant" />
                      <Label htmlFor="comptant" className="cursor-pointer flex-1">
                        <span className="font-medium">Paiement comptant</span>
                        <span className="text-sm text-slate-500 ml-2">1 290€</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#FF6B35] transition-colors cursor-pointer">
                      <RadioGroupItem value="3fois" id="3fois" />
                      <Label htmlFor="3fois" className="cursor-pointer flex-1">
                        <span className="font-medium">Paiement en 3 fois</span>
                        <span className="text-sm text-slate-500 ml-2">3 x 430€</span>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-[#FF6B35] transition-colors cursor-pointer">
                      <RadioGroupItem value="4fois" id="4fois" />
                      <Label htmlFor="4fois" className="cursor-pointer flex-1">
                        <span className="font-medium">Paiement en 4 fois</span>
                        <span className="text-sm text-slate-500 ml-2">4 x 322,50€</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#FF6B35] hover:bg-[#E85D2A] text-white py-6 text-lg font-semibold rounded-xl transition-all"
                  data-testid="landing-submit"
                >
                  {submitting ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Envoi en cours...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      Être recontacté
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  )}
                </Button>

                <p className="text-xs text-slate-400 text-center">
                  En soumettant ce formulaire, vous acceptez d'être recontacté par notre équipe.
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center text-slate-400 text-sm">
          © 2025 WEDO Coaching. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
};

export default LandingFlow;
